import { ReactElement, useState } from "react";
import {
  Typography,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
  Divider,
} from "@mui/material";
import MethodInfo, { InfoTip } from "./FormInfo";

export interface NormalizationFormProps {
  onClose?: () => void;
  onSubmit?: (data: NormalizationData) => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
}

export interface NormalizationData {
  normalizationMethod: string;
}

const NormalizationForm = ({
  onClose,
  onSubmit,
  onBack,
  onContinue,
  className = "",
}: NormalizationFormProps): ReactElement => {
  const [formData, setFormData] = useState<NormalizationData>({
    normalizationMethod: "Median",
  });

  const handleInputChange = (field: keyof NormalizationData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    onSubmit?.(formData);
  };

  return (
    <div
      className={`p-6 h-full flex flex-col justify-between ${className}`}
      style={{
        backgroundColor: "#F5F5F5", // tokens.colors.gray[100]
      }}
    >
      {/* Description */}
      <MethodInfo
        title="Normalization method"
        message="Median scales each cell to the dataset's median total library size; CPM scales each cell to 1,000,000 total counts. Both methods then apply log1p. Gene-wise z-score scaling and batch correction are not applied."
      />

      <Divider className="mb-6" />

      {/* Form Fields */}
      <div className="space-y-6 flex-1">
        <FormControl component="fieldset">
          <RadioGroup
            value={formData.normalizationMethod}
            onChange={(e) =>
              handleInputChange("normalizationMethod", e.target.value)
            }
            className="space-y-4"
          >
            <FormControlLabel
              value="Median"
              control={
                <Radio
                  sx={{
                    color: "#19C1DB",
                    "&.Mui-checked": {
                      color: "#19C1DB",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Median library size
                  </Typography>
                  <InfoTip message="Scales every cell's total counts to the median total count across cells, then applies log1p." />
                </Box>
              }
              sx={{ alignItems: "flex-start" }}
            />

            <FormControlLabel
              value="CPM"
              control={
                <Radio
                  sx={{
                    color: "#19C1DB",
                    "&.Mui-checked": {
                      color: "#19C1DB",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Counts per million (CPM)
                  </Typography>
                  <InfoTip message="Scales every cell's total counts to 1,000,000, then applies log1p." />
                </Box>
              }
              sx={{ alignItems: "flex-start" }}
            />
          </RadioGroup>
        </FormControl>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between mt-6">
        {onBack && (
          <Button
            variant="outlined"
            color="primary"
            onClick={onBack}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1.5,
            }}
          >
            Back
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleSubmit}
          className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            py: 1.5,
          }}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default NormalizationForm;
